/**
 * API client functions for Organization feature
 */

import { apiClient } from '../services/api';
import {
  OrganizationTab,
  OrganizationList,
  AdInList,
  AdSearchResult,
  CreateTabRequest,
  UpdateTabRequest,
  ReorderTabsRequest,
  CreateListRequest,
  UpdateListRequest,
  AddAdsToListRequest,
  MoveAdRequest,
  ReorderAdsRequest,
  GetAllTabsResponse,
  GetListsResponse,
  GetListAdsResponse,
  SearchAdsResponse,
  OrganizationRule,
  CreateRuleRequest,
  UpdateRuleRequest,
  ApplyRuleResponse,
  PreviewRuleRequest,
  PreviewRuleResponse,
  GetRulesResponse,
} from '../types/organization';

// ==================== TAB OPERATIONS ====================

export async function getAllTabs(): Promise<OrganizationTab[]> {
  const { data } = await apiClient.get<GetAllTabsResponse>('/organization/tabs');
  return data.tabs;
}

export async function getTab(tabId: string): Promise<OrganizationTab> {
  const { data } = await apiClient.get<OrganizationTab>(`/organization/tabs/${tabId}`);
  return data;
}

export async function createTab(request: CreateTabRequest): Promise<OrganizationTab> {
  const { data } = await apiClient.post<OrganizationTab>('/organization/tabs', request);
  return data;
}

export async function updateTab(tabId: string, request: UpdateTabRequest): Promise<OrganizationTab> {
  const { data } = await apiClient.put<OrganizationTab>(`/organization/tabs/${tabId}`, request);
  return data;
}

export async function deleteTab(tabId: string): Promise<void> {
  await apiClient.delete(`/organization/tabs/${tabId}`);
}

export async function reorderTabs(request: ReorderTabsRequest): Promise<void> {
  await apiClient.put('/organization/tabs/reorder', request);
}

// ==================== LIST OPERATIONS ====================

export async function getListsForTab(tabId: string): Promise<OrganizationList[]> {
  const { data } = await apiClient.get<GetListsResponse>(`/organization/tabs/${tabId}/lists`);
  return data.lists;
}

export async function getList(listId: string): Promise<OrganizationList> {
  const { data } = await apiClient.get<OrganizationList>(`/organization/lists/${listId}`);
  return data;
}

export async function createList(tabId: string, request: CreateListRequest): Promise<OrganizationList> {
  const { data } = await apiClient.post<OrganizationList>(`/organization/tabs/${tabId}/lists`, request);
  return data;
}

export async function updateList(listId: string, request: UpdateListRequest): Promise<OrganizationList> {
  const { data } = await apiClient.put<OrganizationList>(`/organization/lists/${listId}`, request);
  return data;
}

export async function deleteList(listId: string): Promise<void> {
  await apiClient.delete(`/organization/lists/${listId}`);
}

// ==================== AD OPERATIONS ====================

export async function getListAds(listId: string): Promise<AdInList[]> {
  const { data } = await apiClient.get<GetListAdsResponse>(`/organization/lists/${listId}/ads`);
  return data.ads;
}

export async function addAdsToList(listId: string, request: AddAdsToListRequest): Promise<void> {
  await apiClient.post(`/organization/lists/${listId}/ads`, request);
}

export async function moveAd(listId: string, adId: string, request: MoveAdRequest): Promise<void> {
  await apiClient.put(`/organization/lists/${listId}/ads/${adId}/move`, request);
}

export async function reorderAdsInList(listId: string, request: ReorderAdsRequest): Promise<void> {
  await apiClient.put(`/organization/lists/${listId}/ads/reorder`, request);
}

export async function removeAdFromList(listId: string, adId: string): Promise<void> {
  await apiClient.delete(`/organization/lists/${listId}/ads/${adId}`);
}

export async function searchAds(query: string, tabId: string): Promise<AdSearchResult[]> {
  const { data } = await apiClient.get<SearchAdsResponse>('/organization/ads/search', {
    params: { q: query, tab_id: tabId },
  });
  return data.results;
}

// ==================== RULE OPERATIONS ====================

export async function getRulesForList(listId: string): Promise<OrganizationRule[]> {
  const { data } = await apiClient.get<GetRulesResponse>(`/organization/lists/${listId}/rules`);
  return data.rules;
}

export async function createRule(listId: string, request: CreateRuleRequest): Promise<OrganizationRule> {
  const { data } = await apiClient.post<OrganizationRule>(`/organization/lists/${listId}/rules`, request);
  return data;
}

export async function updateRule(listId: string, ruleId: string, request: UpdateRuleRequest): Promise<OrganizationRule> {
  const { data } = await apiClient.put<OrganizationRule>(`/organization/lists/${listId}/rules/${ruleId}`, request);
  return data;
}

export async function deleteRule(listId: string, ruleId: string): Promise<void> {
  await apiClient.delete(`/organization/lists/${listId}/rules/${ruleId}`);
}

export async function applyRule(listId: string, ruleId: string): Promise<ApplyRuleResponse> {
  const { data } = await apiClient.post<ApplyRuleResponse>(`/organization/lists/${listId}/rules/${ruleId}/apply`);
  return data;
}

export async function previewRule(request: PreviewRuleRequest): Promise<PreviewRuleResponse> {
  const { data } = await apiClient.post<PreviewRuleResponse>('/organization/rules/preview', request);
  return data;
}

export async function getAllRules(): Promise<OrganizationRule[]> {
  const { data } = await apiClient.get<GetRulesResponse>('/organization/rules/all');
  return data.rules;
}
